import { IconButton } from "materia"
import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import type { Jid } from "../../xmpp/jid"
import { ConversationRow } from "./ConversationRow"
import s from "./ConversationList.module.scss"

/** One row's worth of already-resolved display data. */
export type ConversationRowModel = {
  peer: Jid
  name: string
  faces: Parameters<typeof ConversationRow>[0]["faces"]
  preview: string
  timestamp: number
  isUnread: boolean
  isMuted: boolean
}

type ConversationListProps = {
  conversations: ConversationRowModel[]
  /** The conversation on screen, if any — the list marks it. */
  activePeer?: Jid
  onOpen: (peer: Jid) => void
  onNewMessage: () => void
  /** Pinned below the rows, outside the scroll area — the notification prompt. */
  footer?: ReactNode
}

export function ConversationList({
  conversations,
  activePeer,
  onOpen,
  onNewMessage,
  footer,
}: ConversationListProps) {
  const { t } = useTranslation()

  return (
    <div className={s.list}>
      <div className={s.list__header}>
        <h1 className={s.list__title}>{t("chat.LIST_TITLE")}</h1>
        <IconButton
          as="button"
          icon="Compose"
          size="large"
          className={s.list__newMessage}
          aria-label={String(t("chat.NEW_MESSAGE_ARIA"))}
          onClick={onNewMessage}
        />
      </div>

      {conversations.length === 0 ? (
        <p className={s.list__empty}>{t("chat.EMPTY_LIST")}</p>
      ) : (
        <div className={s.list__rows}>
          {conversations.map((conversation) => (
            <div
              className={modifiers(s, "list__row", {
                isActive: conversation.peer === activePeer,
              })}
              key={conversation.peer}
            >
              <ConversationRow
                {...conversation}
                isActive={conversation.peer === activePeer}
                onOpen={() => onOpen(conversation.peer)}
              />
            </div>
          ))}
        </div>
      )}

      {footer}
    </div>
  )
}
